import React, { useEffect, useState } from 'react';
import { FormControl, FormHelperText, InputLabel, MenuItem, Select, Stack, TextField } from '@mui/material';
import {
  formatNpwpInput,
  isCustomerFieldRequired,
  type CustomerFieldErrorMap,
} from '../../customerCompleteness';
import { isPlaceholderCustomerNumber, type CustomerFormDraft } from '../../customerFormDraft';

const titlePrefixOptions = [
  { value: '', label: '(Belum dipilih)' },
  { value: 'MR', label: 'Mr' },
  { value: 'MRS', label: 'Mrs' },
  { value: 'CV', label: 'CV' },
  { value: 'PT', label: 'PT' },
  { value: 'LAINNYA', label: 'Lainnya' },
] as const;

const customerTypeOptions = [
  { value: '', label: '(Belum dipilih)' },
  { value: 'perorangan', label: 'Perorangan' },
  { value: 'perusahaan', label: 'Perusahaan' },
  { value: 'instansi', label: 'Instansi' },
  { value: 'distributor', label: 'Distributor' },
  { value: 'retail', label: 'Retail' },
  { value: 'lainnya', label: 'Lainnya' },
] as const;

const statusOptions = [
  { value: '', label: '(Belum dipilih)' },
  { value: 'active', label: 'Aktif' },
  { value: 'inactive', label: 'Tidak aktif' },
  { value: 'prospect', label: 'Prospek' },
  { value: 'blocked', label: 'Diblokir' },
] as const;

type Props = {
  draft: CustomerFormDraft;
  onChange: (patch: Partial<CustomerFormDraft>) => void;
  readOnly?: boolean;
  /** Pelanggan baru: nomor tidak diisi user; server menetapkan `CUS-#####` saat simpan. */
  isNewCustomer?: boolean;
  fieldErrors?: CustomerFieldErrorMap;
  requiredKeys?: ReadonlySet<keyof CustomerFormDraft>;
  /** Modal pelanggan setuju: nomor pelanggan dikunci dan diisi saat simpan. */
  npwpFormatHint?: boolean;
};

/** Identitas utama: nomor pelanggan, gelar, nama, jenis, status, NPWP. */
export function CustomerFormIdentityTabScreen({
  draft,
  onChange,
  readOnly = false,
  isNewCustomer = false,
  fieldErrors,
  requiredKeys,
  npwpFormatHint = false,
}: Props) {
  const req = (key: keyof CustomerFormDraft) =>
    isCustomerFieldRequired(key, { readOnly, requiredKeys });
  const [npwpText, setNpwpText] = useState(() => formatNpwpInput(draft.npwp));

  useEffect(() => {
    setNpwpText(formatNpwpInput(draft.npwp));
  }, [draft.npwp]);
  return (
    <Stack spacing={2} sx={{ maxWidth: 560 }}>
      <TextField
        label="No. customer"
        value={
          npwpFormatHint && isPlaceholderCustomerNumber(draft.customer_number)
            ? ''
            : draft.customer_number
        }
        onChange={(e) => onChange({ customer_number: e.target.value })}
        fullWidth
        required={req('customer_number') && !isNewCustomer}
        error={Boolean(fieldErrors?.customer_number)}
        disabled={readOnly || isNewCustomer || npwpFormatHint}
        helperText={
          fieldErrors?.customer_number ??
          (npwpFormatHint && isPlaceholderCustomerNumber(draft.customer_number)
            ? 'Kosong akan diisi saat simpan, format CUS.DDMMYY.00001.'
            : isNewCustomer
              ? 'Dibuat otomatis saat disimpan (format CUS-00001, lima digit urut).'
              : 'Nomor unik pelanggan (referensi & pencarian).')
        }
      />
      <FormControl fullWidth size="small" disabled={readOnly} required={req('title_prefix')} error={Boolean(fieldErrors?.title_prefix)}>
        <InputLabel id="cust-title-label">Gelar / awalan</InputLabel>
        <Select
          labelId="cust-title-label"
          label="Gelar / awalan"
          value={draft.title_prefix}
          onChange={(e) => onChange({ title_prefix: e.target.value })}
        >
          {titlePrefixOptions.map((o) => (
            <MenuItem key={o.value || '_empty'} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
        {fieldErrors?.title_prefix ? <FormHelperText>{fieldErrors.title_prefix}</FormHelperText> : null}
      </FormControl>
      <TextField
        label="Nama pelanggan"
        value={draft.name}
        onChange={(e) => onChange({ name: e.target.value })}
        required={req('name')}
        error={Boolean(fieldErrors?.name)}
        helperText={fieldErrors?.name}
        fullWidth
        disabled={readOnly}
        inputProps={{ 'data-testid': 'customer-form-name' }}
      />
      <FormControl fullWidth size="small" disabled={readOnly} required={req('customer_type')} error={Boolean(fieldErrors?.customer_type)}>
        <InputLabel id="cust-type-label">Jenis pelanggan</InputLabel>
        <Select
          labelId="cust-type-label"
          label="Jenis pelanggan"
          id="customer-form-customer-type"
          value={draft.customer_type}
          onChange={(e) => onChange({ customer_type: e.target.value })}
        >
          {customerTypeOptions.map((o) => (
            <MenuItem key={o.value || '_empty'} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
        {fieldErrors?.customer_type ? <FormHelperText>{fieldErrors.customer_type}</FormHelperText> : null}
      </FormControl>
      <FormControl fullWidth size="small" disabled={readOnly} required={req('active_status')} error={Boolean(fieldErrors?.active_status)}>
        <InputLabel id="cust-status-label">Status</InputLabel>
        <Select
          labelId="cust-status-label"
          label="Status"
          id="customer-form-status"
          value={draft.active_status}
          onChange={(e) => onChange({ active_status: e.target.value })}
        >
          {statusOptions.map((o) => (
            <MenuItem key={o.value || '_empty'} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
        {fieldErrors?.active_status ? <FormHelperText>{fieldErrors.active_status}</FormHelperText> : null}
      </FormControl>
      <TextField
        label="NPWP"
        value={npwpText}
        onChange={(e) => {
          const formatted = formatNpwpInput(e.target.value);
          setNpwpText(formatted);
          onChange({ npwp: formatted });
        }}
        required={req('npwp')}
        error={Boolean(fieldErrors?.npwp)}
        helperText={
          fieldErrors?.npwp ??
          (readOnly ? undefined : 'Ketik angka saja. Format otomatis 01.234.567.8-901.000.')
        }
        placeholder="01.234.567.8-901.000"
        fullWidth
        disabled={readOnly}
        inputProps={{ 'data-testid': 'customer-form-npwp', inputMode: 'numeric' }}
      />
    </Stack>
  );
}
